<script lang="ts">
export const iconCls = cva(undefined, {
	variants: {
		size: {
			// We use [svg] before the class to force a higher specifity (0, 1, 1) instead of (0, 1, 0)
			// Parent component, like Badge and Button set the svg size with a specifity of (0, 1, 1)
			// and we are not able to overwrite it otherwise.
			xs: "[svg]:icon-xs",
			sm: "[svg]:icon-sm",
			md: "[svg]:icon-md",
			lg: "[svg]:icon-lg",
			custom: "",

			/**
			 * @deprecated
			 */
			xSmall: "[svg]:icon-xs",

			/**
			 * @deprecated
			 */
			small: "[svg]:icon-sm",

			/**
			 * @deprecated
			 */
			default: "[svg]:icon-md",

			/**
			 * @deprecated
			 */
			medium: "[svg]:icon-md",

			/**
			 * @deprecated
			 */
			large: "[svg]:icon-lg",

			/**
			 * @deprecated
			 */
			xLarge: "[svg]:icon-lg",
		},
	},
});

type Variants = VariantProps<typeof iconCls>;

export interface IconProps extends ClassProps, DeprecatedSizeVariantProps {
	size?: Variants["size"] | (string & {}) | number;
	d?: string;
}

interface Props extends IconProps, /* @vue-ignore */ SVGAttributes {}
</script>

<script setup lang="ts">
import { cva, type VariantProps } from "class-variance-authority";
import {
	computed,
	h,
	useAttrs,
	useSlots,
	warn,
	type CSSProperties,
	type SVGAttributes,
	type VNode,
} from "vue";
import type { ClassProps } from "../../lib/base-props";
import { formatCssDimension } from "../../lib/css";
import { convertDeprecatedSizeVariant, type DeprecatedSizeVariantProps } from "../../lib/size";
import { cn, htmlAttrs } from "../../lib/utils";

defineOptions({ inheritAttrs: false });

const props = defineProps<Props>() as IconProps;
const attrs = useAttrs() as SVGAttributes;

const slots = useSlots();

function nodes(): VNode[] {
	if (props.d != null) {
		return [h("path", { d: props.d })];
	}

	const n = slots.default?.();
	if (n != null && n?.length > 0 && typeof n[0].children === "string") {
		return [h("path", { d: n[0].children })];
	}

	return n || [];
}

function isLegacySize(size: string | number | null | undefined) {
	if (size == null) {
		return false;
	}

	return typeof size === "number" || !Number.isNaN(parseFloat(size));
}

const hasSizeClass = computed(() => {
	if (props.class != null && typeof props.class === "string") {
		return (
			props.class
				.split(" ")
				.find((cls) => /(?:w-(?:\d+|full))|(?:h-(?:\d+|full))|(?:size-(?:\d+|full))/.test(cls)) !=
			null
		);
	}

	return false;
});

const hasCustomSize = computed(() => {
	return hasSizeClass.value || props.size != null;
});

const size = computed(() => {
	if (hasSizeClass.value) {
		return undefined;
	}

	if (isLegacySize(props.size)) {
		return undefined;
	}

	if (props.size != null) {
		return props.size as Variants["size"];
	}

	return convertDeprecatedSizeVariant(props) || "md";
});

const style = computed<CSSProperties>(() => {
	if (props.size == null || !isLegacySize(props.size)) {
		return {};
	}

	if (__DEV__) {
		warn("[UI] Using deprecated size for icon. Please use tailwind utility classes instead.");
	}

	const cssSize = formatCssDimension(props.size);
	return {
		height: cssSize,
		width: cssSize,
		fontSize: cssSize,
	};
});
</script>

<template>
	<svg
		xmlns="http://www.w3.org/2000/svg"
		viewBox="0 0 24 24"
		role="img"
		aria-hidden="true"
		:class="cn(iconCls({ ...props, size }))"
		fill="currentColor"
		:data-custom-size="hasCustomSize"
		:style="style"
		v-bind="htmlAttrs(attrs)"
	>
		<component v-for="(node, idx) in nodes()" :is="node" :key="idx" />
	</svg>
</template>
